import { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';

// Characters to reveal per animation frame (~60fps).
// 5 chars/frame ≈ 300 chars/sec — visible typewriter effect
// that smooths out chunky SSE arrivals.
const CHARS_PER_FRAME = 5;

interface StreamingTextProps {
  content: string;
  isStreaming: boolean;
  components: Record<string, React.ComponentType<any>>;
  className?: string;
}

/**
 * Wraps ReactMarkdown with a client-side typewriter animation.
 * Incoming content (from SSE) is buffered and revealed at a steady
 * character rate, smoothing out the choppy chunk arrivals.
 * Once streaming ends, any remaining text is shown immediately.
 */
export default function StreamingText({
  content,
  isStreaming,
  components,
  className,
}: StreamingTextProps) {
  const [displayedLength, setDisplayedLength] = useState(0);
  const rafRef = useRef<number>(0);
  const targetRef = useRef(content);
  const displayedRef = useRef(0);
  const wasStreamingRef = useRef(false);

  // Keep target in sync
  targetRef.current = content;

  // Reset when a new streaming session begins
  useEffect(() => {
    if (isStreaming && !wasStreamingRef.current) {
      displayedRef.current = 0;
      setDisplayedLength(0);
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming]);

  // When streaming ends, flush all remaining text
  useEffect(() => {
    if (!isStreaming) {
      cancelAnimationFrame(rafRef.current);
      setDisplayedLength(content.length);
      displayedRef.current = content.length;
    }
  }, [isStreaming, content.length]);

  // Animation loop: steadily reveal characters while streaming
  useEffect(() => {
    if (!isStreaming) return;

    const tick = () => {
      const target = targetRef.current.length;
      const current = displayedRef.current;

      if (current < target) {
        const next = Math.min(current + CHARS_PER_FRAME, target);
        displayedRef.current = next;
        setDisplayedLength(next);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafRef.current);
  }, [isStreaming]);

  const visibleContent = isStreaming
    ? content.slice(0, displayedLength)
    : content;

  return (
    <div className={className}>
      <ReactMarkdown components={components}>
        {visibleContent}
      </ReactMarkdown>
    </div>
  );
}
